import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Flame, ThermometerSun, Snowflake, Clock, Calendar, Edit2, History, GripVertical, User, Eye } from 'lucide-react';

export interface Lead {
  id: string;
  contact_name: string;
  studio_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  source: string | null;
  pain_point: string | null;
  current_software: string | null;
  score: 'hot' | 'warm' | 'cold';
  status: 'new' | 'contacted' | 'demo_scheduled' | 'demo_done' | 'converted' | 'lost';
  is_qualified: number;
  is_decision_maker: number;
  demo_scheduled: number;
  demo_date: string | null;
  demo_time: string | null;
  notes: string | null;
  last_contact_date: string | null;
  next_follow_up_date: string | null;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
}

interface LeadCardProps {
  lead: Lead;
  onEdit: (lead: Lead) => void;
  onView: (lead: Lead) => void;
  onViewActivity: (lead: Lead) => void;
  isDragging?: boolean;
  getAssigneeName?: (userId: string | null) => string | null;
}

const scoreConfig = {
  hot: {
    icon: Flame,
    colors: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800',
    borderColor: 'border-l-red-500',
  },
  warm: {
    icon: ThermometerSun,
    colors: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800',
    borderColor: 'border-l-amber-500',
  },
  cold: {
    icon: Snowflake,
    colors: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    borderColor: 'border-l-blue-500',
  },
};

function isOverdue(date: string | null): boolean {
  if (!date) return false;
  return new Date(date) < new Date();
}

function formatDate(date: string | null): string {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function LeadCard({ lead, onEdit, onView, onViewActivity, isDragging, getAssigneeName }: LeadCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: lead.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const config = scoreConfig[lead.score];
  const ScoreIcon = config.icon;
  const overdue = isOverdue(lead.next_follow_up_date);
  const dragging = isDragging || isSortableDragging;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white dark:bg-stone-800 rounded-lg border border-l-4 ${config.borderColor} border-stone-200 dark:border-stone-700 p-3 shadow-sm hover:shadow-md transition-all cursor-grab active:cursor-grabbing ${
        dragging ? 'opacity-50 shadow-lg scale-105 rotate-2' : ''
      } ${overdue ? 'ring-2 ring-red-400 ring-opacity-50' : ''}`}
      {...attributes}
      {...listeners}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <GripVertical className="w-4 h-4 text-stone-400 flex-shrink-0" />
          <div className="min-w-0">
            <h4 className="font-semibold text-stone-900 dark:text-stone-100 truncate text-sm">
              {lead.contact_name}
            </h4>
            {lead.studio_name && (
              <p className="text-xs text-stone-500 dark:text-stone-400 truncate">
                {lead.studio_name}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onView(lead);
            }}
            className="p-1 rounded hover:bg-stone-100 dark:hover:bg-stone-700 text-stone-500 dark:text-stone-400"
            title="View details"
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewActivity(lead);
            }}
            className="p-1 rounded hover:bg-stone-100 dark:hover:bg-stone-700 text-stone-500 dark:text-stone-400"
            title="View activity"
          >
            <History className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(lead);
            }}
            className="p-1 rounded hover:bg-stone-100 dark:hover:bg-stone-700 text-stone-500 dark:text-stone-400"
            title="Edit lead"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Badges */}
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        <span className={`inline-flex items-center gap-1 text-xs font-bold px-1.5 py-0.5 rounded border ${config.colors}`}>
          <ScoreIcon className="w-3 h-3" />
          {lead.score.toUpperCase()}
        </span>
        {overdue && (
          <span className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
            <Clock className="w-3 h-3" />
            Overdue
          </span>
        )}
      </div>

      {/* Info row */}
      <div className="flex flex-wrap items-center gap-2 text-xs text-stone-500 dark:text-stone-400">
        {lead.assigned_to && getAssigneeName && getAssigneeName(lead.assigned_to) && (
          <span className="flex items-center gap-1 text-indigo-600 dark:text-indigo-400 font-medium">
            <User className="w-3 h-3" />
            {getAssigneeName(lead.assigned_to)}
          </span>
        )}
        {lead.next_follow_up_date && (
          <span className={`flex items-center gap-1 ${overdue ? 'text-red-600 dark:text-red-400 font-medium' : ''}`}>
            <Clock className="w-3 h-3" />
            {formatDate(lead.next_follow_up_date)}
          </span>
        )}
        {lead.demo_date && (
          <span className="flex items-center gap-1 text-sage">
            <Calendar className="w-3 h-3" />
            Demo: {formatDate(lead.demo_date)}
          </span>
        )}
        {(lead.city || lead.state || lead.country) && (
          <span className="truncate max-w-[120px]">
            {[lead.city, lead.state || lead.country].filter(Boolean).join(', ')}
          </span>
        )}
      </div>
    </div>
  );
}

// Preview component for drag overlay
export function LeadCardPreview({ lead }: { lead: Lead }) {
  const config = scoreConfig[lead.score];
  const ScoreIcon = config.icon;

  return (
    <div className={`bg-white dark:bg-stone-800 rounded-lg border border-l-4 ${config.borderColor} border-stone-200 dark:border-stone-700 p-3 shadow-xl w-[180px] rotate-3 scale-105`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="min-w-0">
          <h4 className="font-semibold text-stone-900 dark:text-stone-100 truncate text-sm">
            {lead.contact_name}
          </h4>
          {lead.studio_name && (
            <p className="text-xs text-stone-500 dark:text-stone-400 truncate">
              {lead.studio_name}
            </p>
          )}
        </div>
      </div>
      <span className={`inline-flex items-center gap-1 text-xs font-bold px-1.5 py-0.5 rounded border ${config.colors}`}>
        <ScoreIcon className="w-3 h-3" />
        {lead.score.toUpperCase()}
      </span>
    </div>
  );
}
